import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import {  type InstallMethod, INSTALL_LABELS } from '@/features/integrations/components/GuideNotes'
import { CLI_REPO, CLI_INSTALL_SH } from '@/features/integrations/snippets'

export function CliGettingStartedGuide({ org, onSwitchGuide }: { org: OrgItem | null; onSwitchGuide: (id: GuideId) => void }) {
  const [method, setMethod] = useState<InstallMethod>('brew')
  const orgSlug = org?.slug ?? '<org-slug>'

  const quickstart = `terramantle auth login                 # device flow; auto-detected in CI
terramantle context set ${orgSlug} --org ${orgSlug} --workspace prod
terramantle context use ${orgSlug}       # pick the default org/workspace

terramantle providers ls               # providers in use, Trust verdicts inline
terramantle modules search database    # search the registry
terramantle lock push                  # upload ./.terraform.lock.hcl`

  return (
    <div>
      <h3 style={s.guideTitle}>Terramantle CLI</h3>
      <p style={s.para}>
        <code style={s.code}>terramantle</code> is a single static binary that wraps the Terramantle
        registry API in a <code style={s.code}>kubectl</code>-style, resource-first interface. One tool,
        two modes - interactive for humans, non-interactive for CI - and one auth model across both. It is
        open source at{' '}
        <a href={CLI_REPO} target="_blank" rel="noopener noreferrer">github.com/terramantle/terramantle-cli</a>.
      </p>

      <h3 style={s.subTitle}>Why use it</h3>
      <ul style={s.list}>
        <li><strong>No token plumbing.</strong> <code style={s.code}>auth login</code> once, then{' '}
          <code style={s.code}>eval "$(terramantle auth env --terraform)"</code> exports{' '}
          <code style={s.code}>TF_HTTP_PASSWORD</code> and the registry <code style={s.code}>TF_TOKEN_*</code>{' '}
          variables - no hand-rolled OIDC exchange, no <code style={s.code}>~/.terraformrc</code> editing.</li>
        <li><strong>No stored CI secret.</strong> In GitHub Actions and GitLab CI it picks up the runner's
          ambient OIDC identity. See <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-ci')}>Using in CI</button>.</li>
        <li><strong>Discovery with Trust inline.</strong> <code style={s.code}>providers ls</code>,{' '}
          <code style={s.code}>modules search</code> and <code style={s.code}>state ls</code> answer from the
          terminal, with the Trust Seal verdict on the row.</li>
        <li><strong>Scriptable.</strong> <code style={s.code}>-o json|yaml|wide</code>, stable exit codes, and{' '}
          <code style={s.code}>NO_COLOR</code> / non-TTY awareness.</li>
        <li><strong>Repo scaffolding.</strong> <code style={s.code}>init</code> and{' '}
          <code style={s.code}>upgrade</code> lay down a module or workspace repo with its CI pipeline already
          wired up.</li>
      </ul>

      <h3 style={s.subTitle}>Install</h3>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {(Object.keys(INSTALL_LABELS) as InstallMethod[]).map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className="btn btn-ghost btn-sm"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '0.68rem',
              opacity: method === m ? 1 : 0.45,
              borderBottom: method === m ? '1px solid var(--accent)' : '1px solid transparent',
              borderRadius: 0,
              paddingBottom: 5,
            }}
          >
            {INSTALL_LABELS[m]}
          </button>
        ))}
      </div>
      {method === 'brew' && (
        <ShellSnippet code={`brew install terramantle/tap/terramantle

# or, if you prefer to tap first
brew tap terramantle/tap && brew install terramantle`} language="bash" />
      )}
      {method === 'shell' && <ShellSnippet code={CLI_INSTALL_SH} language="bash" />}
      {method === 'source' && (
        <ShellSnippet code={`git clone ${CLI_REPO}
cd terramantle-cli
cargo build --release
# binary at target/release/terramantle`} language="bash" />
      )}
      <p style={{ ...s.para, marginTop: 10 }}>
        Released binaries cover macOS (arm64, x86_64), Linux (x86_64, arm64) and Windows (x86_64). Building
        from source needs a stable Rust toolchain. Confirm the install with{' '}
        <code style={s.code}>terramantle version</code>.
      </p>

      <h3 style={s.subTitle}>Quickstart</h3>
      <ShellSnippet code={quickstart} language="bash" />
      <p style={{ ...s.para, marginTop: 10 }}>
        Next: <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-auth')}>Signing In</button> covers the
        auth modes and the <code style={s.code}>eval</code> trick, and{' '}
        <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-commands')}>Commands</button> walks the rest
        of the surface.
      </p>

      <h3 style={s.subTitle}>Shell completions</h3>
      <ShellSnippet code={`terramantle completion bash > /etc/bash_completion.d/terramantle
terramantle completion zsh  > "\${fpath[1]}/_terramantle"
terramantle completion fish > ~/.config/fish/completions/terramantle.fish`} language="bash" />

      <h3 style={s.subTitle}>Jumping back to the portal</h3>
      <p style={s.para}>
        <code style={s.code}>console</code> opens this portal in your default browser - handy when a Trust
        verdict needs a closer look. On a headless box the URL is printed instead of failing.
      </p>
      <ShellSnippet code={`terramantle console                      # the portal root
terramantle console org/${orgSlug}/modules
terramantle console --print              # print the URL, don't launch`} language="bash" />
    </div>
  )
}
